import { rgbaUint32ToCss } from '@/utils/color-kit';
import { createSVGElement, XLINK_NS } from '@/utils/svg-kit';
import type { Bounds } from '@/version-types';
import type { ReadyTextRenderListMarker } from '../../assets';
import type { TextRenderGraphicListMarker, TextRenderSVGListMarker } from '../../types';
import type { RenderContext } from '../context';
import type { GlyphItem } from '../glyph';
import { buildListMarkerGeometry } from './geometry';
import type { ListMarkerShaping } from './shape';

type ResolvedGraphicMarker =
    | {
          kind: 'image';
          marker: Extract<TextRenderGraphicListMarker, { kind: 'image' }>;
          asset: Extract<ReadyTextRenderListMarker, { type: 'image' }>;
      }
    | {
          kind: 'svg';
          marker: TextRenderSVGListMarker;
          asset: Extract<ReadyTextRenderListMarker, { type: 'svg' }>;
      };

/**
 * 当前一轮列表 marker 视觉准备结果。
 */
export type ListMarkerVisual = Readonly<{
    bounds: Bounds | null;
    markerPaths: GlyphItem[][];
    drawGraphic?: (target: SVGElement) => void;
}>;

/**
 * 找到当前图形型 marker 声明对应的 ready 素材。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param getMarker - 只读取列表 marker 素材的具名 getter。
 * @returns marker 声明和 ready 素材；未声明、未 ready 或形态不匹配时返回 null。
 */
const resolveGraphicMarker = (
    context: RenderContext,
    getMarker?: (marker: TextRenderGraphicListMarker) => ReadyTextRenderListMarker | undefined,
): ResolvedGraphicMarker | null => {
    const marker = context.input.listMarker;
    if (!marker || marker.kind === 'character' || !getMarker) return null;
    const asset = getMarker(marker);
    if (marker.kind === 'image' && asset?.type === 'image') return { kind: 'image', marker, asset };
    if (marker.kind === 'svg' && asset?.type === 'svg') return { kind: 'svg', marker, asset };
    return null;
};

/**
 * 把 ready SVG marker 变成固定 viewport 的可嵌入 data URL。
 *
 * 图形型 marker 延续既有行为：只替换颜色槽并设置 viewport，不额外清洗或校验素材。
 *
 * @param svgText - 已加载 SVG 源码。
 * @param marker - SVG marker 的 viewport 与颜色槽声明。
 * @returns 可序列化到输出 SVG 的 data URL。
 */
const createSVGMarkerDataURL = (svgText: string, marker: TextRenderSVGListMarker): string => {
    let substituted = svgText;
    (marker.iconColors ?? []).forEach((entry) => {
        entry.values.forEach((value) => {
            substituted = substituted.split(`{{colors[${value}]}}`).join(entry.color);
        });
    });
    const parsed = new DOMParser().parseFromString(substituted, 'image/svg+xml');
    const root = parsed.documentElement;
    root.setAttribute('width', String(marker.width));
    root.setAttribute('height', String(marker.height));
    if (!root.getAttribute('viewBox')) root.setAttribute('viewBox', `0 0 ${marker.width} ${marker.height}`);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(root.outerHTML)}`;
};

/**
 * 为当前一轮列表 marker 准备边界、字符 marker path 和图形素材绘制函数。
 *
 * @param context - 当前一轮不变的输入、snapshot 与 SVG 命名空间。
 * @param shaping - 与 snapshot 同轮的列表 marker 塑形事实。
 * @param getMarker - 只读取列表 marker 素材的具名 getter。
 * @returns 存在 marker 几何时的视觉产出，否则返回 undefined。
 */
export const resolveListMarker = (
    context: RenderContext,
    shaping: ListMarkerShaping | null,
    getMarker?: (marker: TextRenderGraphicListMarker) => ReadyTextRenderListMarker | undefined,
): ListMarkerVisual | undefined => {
    const geometry = buildListMarkerGeometry(context.input, context.snapshot, shaping);
    if (!geometry) return undefined;

    if (geometry.type === 'character') {
        const fill = rgbaUint32ToCss(context.input.colorRgba);
        const markerPaths = geometry.items.map(({ paths, rotation }) =>
            paths.map<GlyphItem>((path) => ({
                glyph: { kind: 'marker', d: path.d, fill, transform: path.transform },
                spanIndex: -1,
                transform: rotation ?? undefined,
            })),
        );
        return { bounds: geometry.bounds, markerPaths };
    }

    // 素材身份在准备阶段只解析一次；未 ready 时既不绘制，也不把预计算几何并入可见边界。
    const resolved = resolveGraphicMarker(context, getMarker);
    if (!resolved || geometry.width <= 0 || geometry.height <= 0) {
        return { bounds: null, markerPaths: [] };
    }
    const href =
        resolved.kind === 'image'
            ? resolved.asset.dataURL
            : createSVGMarkerDataURL(resolved.asset.svgText, resolved.marker);

    return {
        bounds: geometry.bounds,
        markerPaths: [],
        drawGraphic: (target) => {
            const group = createSVGElement('g', { 'data-list-marker': 'graphic' });
            geometry.items.forEach(({ position, numberPaths }) => {
                const image = createSVGElement(
                    'image',
                    {
                        href,
                        x: position.x,
                        y: position.y,
                        width: geometry.width,
                        height: geometry.height,
                    },
                    { [XLINK_NS]: { 'xlink:href': href } },
                );
                group.appendChild(image);

                if (resolved.kind !== 'svg' || numberPaths.length === 0) return;

                const numberGroup = createSVGElement('g', { 'data-list-marker-number': '' });
                numberPaths.forEach((path) => {
                    const node = createSVGElement('path', {
                        d: path.d,
                        fill: resolved.marker.fontColor ?? '#000000',
                        transform: path.transform,
                    });
                    numberGroup.appendChild(node);
                });

                group.appendChild(numberGroup);
            });

            target.appendChild(group);
        },
    };
};
